import { type PointerEvent, type ReactNode, useEffect, useRef } from 'react';
import { useBackLayer } from '../hooks/useBackLayer';

interface Props {
  children: ReactNode;
  /** Close on backdrop tap, Escape and the browser back button. Default true. */
  dismissible?: boolean;
  onClose?: () => void;
  tall?: boolean;
  label: string;
}

/** Downward release speed (px/ms) that closes a sheet however short the drag. */
const DISMISS_VELOCITY = 0.5;

/** Modal bottom sheet. Dismissible sheets also close when their handle is dragged down. Non-dismissible sheets (the find sheet) only close through their own actions. */
export function Sheet({ children, dismissible = true, onClose, tall = false, label }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
    if (!dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismissible, onClose]);

  const drag = useRef<{ startY: number; lastY: number; lastT: number; velocity: number } | null>(null);

  const setOffset = (dy: number, animate: boolean) => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
    el.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
  };

  const onDown = (e: PointerEvent) => {
    drag.current = { startY: e.clientY, lastY: e.clientY, lastT: Date.now(), velocity: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dt = Date.now() - d.lastT;
    if (dt > 0) d.velocity = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY;
    d.lastT = Date.now();
    setOffset(Math.max(0, e.clientY - d.startY), false);
  };
  const onUp = (e: PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const dy = Math.max(0, e.clientY - d.startY);
    const height = ref.current?.offsetHeight || 600;
    if (e.type !== 'pointercancel' && (dy > height / 4 || (dy > 12 && d.velocity > DISMISS_VELOCITY))) onClose?.();
    else setOffset(0, true);
  };

  return (
    <div className="backdrop" onClick={dismissible ? onClose : undefined} data-testid="sheet-backdrop">
      {dismissible && <BackLayer onBack={() => onClose?.()} />}
      <div ref={ref} className={`sheet${tall ? ' tall' : ''}`} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        {dismissible ? (
          <div className="sheet-grip" data-testid="sheet-grip" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
            <div className="handle" />
          </div>
        ) : (
          <div className="handle" />
        )}
        {children}
      </div>
    </div>
  );
}

function BackLayer({ onBack }: { onBack: () => void }) {
  useBackLayer(onBack);
  return null;
}
